import { useMemo } from "react"
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from "recharts"

import { Card, CardAction, CardContent, CardHeader, CardTitle } from "./Card"
import { ChartContainer } from "./Chart"
import { useIsMobile } from "../lib/use-is-mobile"

export type LinhaDeDados = {
  chave: string
  rotulo: string
  contratos: number
  valorFormatado: string
  tom: "critico" | "atencao" | "informacao"
}

const COR_DO_TOM: Record<LinhaDeDados["tom"], string> = {
  critico: "var(--vermelho)",
  atencao: "var(--ambar)",
  informacao: "var(--azul-200)",
}

function TickDaFaixa(props: {
  x?: number
  y?: number
  payload?: { value?: string }
  porChave: Map<string, LinhaDeDados>
}) {
  const { x = 0, y = 0, payload, porChave } = props
  const item = payload?.value !== undefined ? porChave.get(payload.value) : undefined
  if (!item) return null
  return (
    <g transform={`translate(${x},${y})`}>
      <circle cx={0} cy={10} r={4} fill={COR_DO_TOM[item.tom]} />
      <text x={8} y={14} textAnchor="start" className="fill-muted-foreground text-[0.6875rem]">
        {item.rotulo}
      </text>
      <text x={8} y={30} textAnchor="start" className="fill-foreground text-[0.6875rem] font-semibold">
        {item.valorFormatado}
      </text>
    </g>
  )
}

function TickDaFaixaNaLateral(props: {
  x?: number
  y?: number
  payload?: { value?: string }
  porChave: Map<string, LinhaDeDados>
}) {
  const { x = 0, y = 0, payload, porChave } = props
  const item = payload?.value !== undefined ? porChave.get(payload.value) : undefined
  if (!item) return null
  return (
    <g transform={`translate(${x},${y})`}>
      <circle cx={-122} cy={0} r={4} fill={COR_DO_TOM[item.tom]} />
      <text x={-6} y={-2} textAnchor="end" className="fill-muted-foreground text-[0.6875rem]">
        {item.rotulo}
      </text>
      <text x={-6} y={12} textAnchor="end" className="fill-foreground text-[0.6875rem] font-semibold">
        {item.valorFormatado}
      </text>
    </g>
  )
}

function BarraClicavel(props: {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: LinhaDeDados
  chaveSelecionada: string | null
  aoSelecionar: (chave: string) => void
  tituloDoClique: (item: LinhaDeDados, selecionada: boolean) => string
}) {
  const { x = 0, y = 0, width = 0, height = 0, payload, chaveSelecionada, aoSelecionar, tituloDoClique } = props
  if (!payload) return null
  const selecionada = payload.chave === chaveSelecionada
  const clicar = () => aoSelecionar(payload.chave)
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={selecionada ? "var(--marca-selecionada)" : "var(--primary)"}
      role="button"
      tabIndex={0}
      aria-pressed={selecionada}
      aria-label={payload.rotulo}
      style={{ cursor: "pointer", outline: "none" }}
      onClick={clicar}
      onKeyDown={(evento) => {
        if (evento.key === "Enter" || evento.key === " ") {
          evento.preventDefault()
          clicar()
        }
      }}
    >
      <title>{tituloDoClique(payload, selecionada)}</title>
    </rect>
  )
}

export function layoutDoGrafico(
  linhas: number,
  celular: boolean,
): { layout: "horizontal" | "vertical"; altura: number } {
  if (!celular) return { layout: "horizontal", altura: 224 }
  return { layout: "vertical", altura: linhas * 36 + 40 }
}

export function BarChartCard({
  titulo,
  descricao,
  dados,
  chaveSelecionada,
  aoSelecionar,
  tituloDoClique,
  className,
}: {
  titulo: string
  descricao?: React.ReactNode
  dados: LinhaDeDados[]
  chaveSelecionada: string | null
  aoSelecionar: (chave: string) => void
  tituloDoClique: (item: LinhaDeDados, selecionada: boolean) => string
  className?: string
}) {
  const porChave = useMemo(() => new Map(dados.map((item) => [item.chave, item])), [dados])
  const { layout, altura } = layoutDoGrafico(dados.length, useIsMobile())
  const naLateral = layout === "vertical"

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{titulo}</CardTitle>
        {descricao !== undefined && <CardAction>{descricao}</CardAction>}
      </CardHeader>
      <CardContent>
        <ChartContainer config={{}} className="aspect-auto w-full" style={{ height: altura }}>
          <BarChart
            data={dados}
            layout={layout}
            margin={naLateral ? { top: 4, right: 36, bottom: 4, left: 0 } : { top: 20, bottom: 36 }}
          >
            <CartesianGrid vertical={naLateral} horizontal={!naLateral} stroke="var(--border)" />
            {naLateral ? (
              <>
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="chave"
                  width={132}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  tick={(props: object) => <TickDaFaixaNaLateral {...props} porChave={porChave} />}
                />
              </>
            ) : (
              <XAxis
                dataKey="chave"
                tickLine={false}
                axisLine={false}
                interval={0}
                height={40}
                tick={(props: object) => <TickDaFaixa {...props} porChave={porChave} />}
              />
            )}
            <Bar
              dataKey="contratos"
              shape={(props: object) => (
                <BarraClicavel
                  {...props}
                  chaveSelecionada={chaveSelecionada}
                  aoSelecionar={aoSelecionar}
                  tituloDoClique={tituloDoClique}
                />
              )}
            >
              <LabelList
                dataKey="contratos"
                position={naLateral ? "right" : "top"}
                className="fill-foreground text-xs font-semibold"
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
